import {
  Box,
  Button as MuiButton,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import LinearProgress, {
  type LinearProgressProps,
} from "@mui/material/LinearProgress";
import {
  LinkButton,
  ButtonVariantOption,
  SubmitButton,
  Button,
} from "../../components/Button";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { FeedGrid } from "./components/FeedGrid";
import { KeyboardArrowLeft } from "@mui/icons-material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import { useAcquireBookStatus } from "./hooks/useAcquireBookStatus";
import { type AcquireBookStatus } from "@/features/acquireBookStatus/types";
import { useApiClient } from "@/hooks/useApiClient";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { type HandOffMessage } from "@/types/handOffMessage";
import { useFeatureFlags } from "@/configs/featureFlag";
import Tooltip from "@mui/material/Tooltip";
import { acquireBookIsDone } from "@/utils/utilsBookStatus";
import { getPrefectureIdByValue } from "@/utils/utilsAcquireBook";
import { AcquireBookModal } from "./components/AcquireBookModal";
import { type ISingleBookAcquireDialogData } from "../../types/singleBookAcquireDialog";
import { AcquireBookEditModal } from "@/features/acquireBookStatus/components/AcquireBookEditModal";
import { type GridSelectionModel } from "@mui/x-data-grid";
import {
  type BookTypeEng,
  type AcquireMultipleBookHandoffData,
} from "@/types/acquirebook";
import { ConfirmAnalyzeDialog } from "./components/ConfirmAnalyzeDialog";

export const AcquireBookStatusPresenter: React.FC = () => {
  const { id } = useParams();

  const { data, getStatusString, getRemainingCountWithErrorCount, mutate } =
    useAcquireBookStatus(id);
  const [remainingCount] = getRemainingCountWithErrorCount(data);
  const navigate = useNavigate();
  const { apiClient } = useApiClient();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [progress, setProgress] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [acquisitionCompletionTime, setAcquisitionCompletionTime] = useState<
    number | null
  >(null);
  const [startTime] = useState(new Date());
  const acquireBookDetails: AcquireBookStatus[] = data?.list ?? [];
  const [selectedOwnerInfos, setSelectedOwnerInfos] = useState<
    Map<number, boolean>
  >(
    acquireBookDetails.reduce<Map<number, boolean>>((a, x) => {
      a.set(x.id, false);
      return a;
    }, new Map<number, boolean>())
  );
  const [selectedAllMattersInfos, setSelectedAllMattersInfos] = useState<
    Map<number, boolean>
  >(
    acquireBookDetails.reduce<Map<number, boolean>>((a, x) => {
      a.set(x.id, false);
      return a;
    }, new Map<number, boolean>())
  );

  const [singleBookAcquireDialogData, setSingleBookAcquireDialogData] =
    useState<ISingleBookAcquireDialogData>({
      prefecturesId: 0,
      prefectures: "",
      address: "",
      chiban: "",
      kaokuNumber: "",
      realEstateTypeEng: "",
    });

  const [openOwnerAnalyzeDialog, setOpenOwnerAnalyzeDialog] =
    useState<boolean>(false);
  const [openAllMattersAnalyzeDialog, setOpenAllMattersAnalyzeDialog] =
    useState<boolean>(false);

  const handleOwnerAnalyzeButtonClick = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    const detailIds: number[] = [];
    for (const [detailId, checked] of selectedOwnerInfos) {
      if (checked) {
        detailIds.push(detailId);
      }
    }
    const body = {
      detailIds,
    };
    apiClient.post("/owner-analyze/picture-request", body).catch((e) => {
      Sentry.captureException(e);
      cancelLoading();
      toast.error("所有者事項解析に失敗しました");
    });

    // 上のapiClient.post()はawaitしてないしthen()も無いため、成功しても失敗してもすぐに下のコードが実行される

    const handOffMessage: HandOffMessage = {
      command: "OWNER_INFO_SHOW_ACCEPT_MESSAGE",
    };

    // 成功した場合は次の画面へ遷移するのでcancelLoading()の呼び出しは不要
    navigate("/ownerinfo/list", {
      replace: true,
      state: { handOffMessage },
    });
  };

  const handleAllMattersAnalyzeButtonClick = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    const detailIds: number[] = [];
    for (const [detailId, checked] of selectedAllMattersInfos) {
      if (checked) {
        detailIds.push(detailId);
      }
    }
    const body = {
      detailIds,
    };
    apiClient.post("/all-matters-analyze/picture-request", body).catch((e) => {
      Sentry.captureException(e);
      cancelLoading();
      toast.error("全部事項解析に失敗しました");
    });

    // 上のapiClient.post()はawaitしてないしthen()も無いため、成功しても失敗してもすぐに下のコードが実行される
    const handOffMessage: HandOffMessage = {
      command: "ALL_MATTERS_SHOW_ACCEPT_MESSAGE",
    };

    // 成功した場合は次の画面へ遷移するのでcancelLoading()の呼び出しは不要
    navigate("/allmatters/list", {
      replace: true,
      state: { handOffMessage },
    });
  };

  const acStatusMessage = getStatusString(data);
  // data?.countが途中でundefinedから10とか5とかに変化するけど、それに反応してくれない、ここはバックエンドからの値によって定期的に変化するので更新には追従してほしい
  const drawingCount = remainingCount ?? 0; // 図面数
  const estimatedTimePerDrawing = 600; // 図面1個あたりの予測時間(秒)(仮置き)
  // TODO 見た目を確認するための仮置き
  // １秒ごとに処理を実行して経過時間を更新しているため、再読み込みしなくても経過時間が更新される
  const updateProgressAndTime = (
    setAcquisitionCompletionTime: React.Dispatch<
      React.SetStateAction<number | null>
    >,
    setProgress: React.Dispatch<React.SetStateAction<number>>
  ): void => {
    const now = new Date();
    const elapsedTime = Math.floor(
      (now.getTime() - startTime.getTime()) / 60000
    ); // 経過時間(分)
    const estimatedTotalTime = (estimatedTimePerDrawing / 60) * drawingCount; // 所要時間予測(分)
    const remainingTime = Math.max(estimatedTotalTime - elapsedTime, 0); // 取得完了見込み時間(分)
    setAcquisitionCompletionTime(remainingTime);
    setProgress((elapsedTime / estimatedTotalTime) * 100);
  };

  useEffect(() => {
    updateProgressAndTime(setAcquisitionCompletionTime, setProgress);
    const interval: number = window.setInterval(() => {
      updateProgressAndTime(setAcquisitionCompletionTime, setProgress);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [startTime]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const LinearProgressWithLabel: React.FC<
    LinearProgressProps & { value: number }
  > = (props: LinearProgressProps & { value: number }) => {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress variant="determinate" {...props} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography
            fontWeight={"bold"}
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "15px", color: "blue" }}
          >{`${Math.round(props.value)}%`}</Typography>
        </Box>
      </Box>
    );
  };

  const shouldShowTooltip = (): boolean => {
    // 処理が完了しているかつzipFileUriが存在しない場合はtrue
    if (acStatusMessage === "") {
      return false;
    }
    return acquireBookIsDone(acStatusMessage) && !data?.zipFileUri;
  };

  const { ownerInfoViaScraping, mypageTop, displayCheckboxAllMattersAnalyze } =
    useFeatureFlags();
  const [acquireBookOpen, setAcquireBookOpen] = useState<boolean>(false);
  const [openAcquireBookNameDialog, setOpenAcquireBookNameDialog] =
    useState<boolean>(false);
  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>([]);

  // 登記/図面取得ボタンが押された時の処理
  const handleAcquireMultipleBooksClick = (): void => {
    if (!data?.list || !selectedRowIds.length) return;

    const multipleBookAcquireData: AcquireMultipleBookHandoffData[] = data.list
      .filter((row) => selectedRowIds.includes(row.id))
      .map((row) => ({
        id: row.id,
        bookType: row.realEstateType as BookTypeEng,
        prefCode: String(getPrefectureIdByValue(row.prefectureName) ?? 0),
        prefName: row.prefectureName,
        locationFull: row.location,
        chibanKaokuNumber: row.chibanKaokuNumber,
        kyoutan: false,
        sintaku: false,
        genzai: false,
        ownerInfo: false,
        electricDrawings: false,
        tisekiDrawings: false,
        tiekiDrawings: false,
        buildingDrawings: false,
      }));

    if (multipleBookAcquireData.length) {
      // feedのテーブルが1件以上選択されている場合複数登記取得画面へ遷移
      navigate("/acquiremultiplebooks", {
        state: multipleBookAcquireData,
      });
    } else {
      // feedのテーブルが選択されていない場合単体登記取得画面へ遷移
      navigate("/acquirebook", { state: {} });
    }
  };

  // 識別名を更新後、すぐにデータの再フェッチを行う
  const handleRefreshAfterEdit = (): void => {
    mutate(data).catch(() => {
      toast.error("データの再取得に失敗しました。");
    });
  };

  return (
    <PagePaper>
      <form>
        <Box sx={{ display: "flex" }}>
          {/* ページタイトル */}
          <Box sx={{ display: "flex", mb: 3 }}>
            <RequestQuoteIcon sx={{ mr: 1 }} fontSize="large" />
            <PageTitle>取得済不動産登記の詳細を確認する</PageTitle>
          </Box>
        </Box>
        <Grid
          container
          spacing={2}
          sx={{ mb: 2 }}
          justifyContent={"space-between"}
          alignItems={"flex-end"}
        >
          <Grid item xs={6} sx={{ ml: 0 }}>
            <Typography
              sx={{ color: "black", fontSize: "20px", fontWeight: "bold" }}
            >
              識別名: {data?.name}
            </Typography>
            <Typography
              fontWeight={"bold"}
              sx={{ color: "#4A86DF", fontSize: "20px" }}
            >
              ステータス: {acStatusMessage}
            </Typography>
          </Grid>
          {/* 一時非表示にする */}
          {/* <Grid item xs> */}
          {/* <Stack sx={{ width: "50%" }}> */}
          {/* <Stack direction="row"> */}
          {/*   <Typography fontWeight={"bold"} style={{ color: "#4A86DF" }}> */}
          {/*     取得完了見込み時間: */}
          {/*   </Typography> */}
          {/*   <Typography */}
          {/*     display="inline" */}
          {/*     fontWeight={"bold"} */}
          {/*     style={{ color: "#4A86DF" }} */}
          {/*   > */}
          {/*     残り */}
          {/*     {acquisitionCompletionTime === null */}
          {/*       ? "" */}
          {/*       : `${acquisitionCompletionTime}分`} */}
          {/*   </Typography> */}
          {/* </Stack> */}

          {/* <Box> */}
          {/*   <LinearProgressWithLabel value={progress} /> */}
          {/* </Box> */}
          {/* </Stack> */}
          {/* </Grid> */}
        </Grid>
        <Grid container sx={{ mb: 2 }}>
          <Grid item xs={1} sx={{ textAlign: "left" }}>
            <MuiButton
              variant="outlined"
              component={Link}
              onClick={() => {
                setOpenAcquireBookNameDialog(true);
              }}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              識別名編集
            </MuiButton>
          </Grid>
        </Grid>
        <SectionPaper>
          <FeedGrid
            row={data?.list}
            selectedOwnerInfos={selectedOwnerInfos}
            setSelectedOwnerInfos={setSelectedOwnerInfos}
            bindOwnerAnalyzeRequestId={data?.bindOwnerAnalyzeRequestId}
            selectedAllMattersInfos={selectedAllMattersInfos}
            setSelectedAllMattersInfos={setSelectedAllMattersInfos}
            bindAllMattersAnalyzeRequestId={
              data?.bindAllMattersAnalyzeRequestId
            }
            setAcquireBookOpen={setAcquireBookOpen}
            setSingleBookAcquireDialogData={setSingleBookAcquireDialogData}
            selectedRowIds={selectedRowIds}
            setSelectedRowIds={setSelectedRowIds}
          />
          <Box sx={{ mb: 6 }} />
          <Stack direction={"row"} spacing={4} justifyContent={"center"} mb={2}>
            <Tooltip
              title={
                shouldShowTooltip() ? (
                  <>
                    一括ダウンロード用のファイルを作成中です。
                    <br />
                    数分間かかる場合があります。
                  </>
                ) : (
                  ""
                )
              }
              placement="top"
              arrow
              disableHoverListener={!shouldShowTooltip()}
            >
              <span>
                <LinkButton
                  variant={ButtonVariantOption.Outlined}
                  disabled={!data?.zipFileUri}
                  href={data?.zipFileUri ?? ""}
                >
                  一括ダウンロードする
                </LinkButton>
              </span>
            </Tooltip>
            <Button
              label={"選択した登記の再取得に進む"}
              variant={ButtonVariantOption.Outlined}
              onClick={handleAcquireMultipleBooksClick}
              disabled={!selectedRowIds.length}
            />
            {ownerInfoViaScraping && data?.bindOwnerAnalyzeRequestId === null && (
              <Button
                // 1件でも解析にチェックがあれば有効とする
                disabled={
                  ![...selectedOwnerInfos.values()].some((value) => value)
                }
                variant={ButtonVariantOption.Outlined}
                onClick={() => {
                  setOpenOwnerAnalyzeDialog(true);
                }}
                label={"所有者事項を解析する"}
              />
            )}
            {ownerInfoViaScraping && data?.bindOwnerAnalyzeRequestId && (
              <MuiButton
                variant={ButtonVariantOption.Outlined}
                component={RouterLink}
                to={`/ownerinfo/detail/${data?.bindOwnerAnalyzeRequestId}`}
              >
                解析済みの所有者事項を確認する
              </MuiButton>
            )}
            {displayCheckboxAllMattersAnalyze &&
              data?.bindAllMattersAnalyzeRequestId === null && (
                <Button
                  // 1件でも解析にチェックがあれば有効とする
                  disabled={
                    ![...selectedAllMattersInfos.values()].some(
                      (value) => value
                    )
                  }
                  variant={ButtonVariantOption.Outlined}
                  onClick={() => {
                    setOpenAllMattersAnalyzeDialog(true);
                  }}
                  label={"全部事項を解析する"}
                />
              )}
            {displayCheckboxAllMattersAnalyze &&
              data?.bindAllMattersAnalyzeRequestId && (
                <MuiButton
                  variant={ButtonVariantOption.Outlined}
                  component={RouterLink}
                  to={`/allmatters/detail/${data?.bindAllMattersAnalyzeRequestId}`}
                >
                  解析済みの全部事項を確認する
                </MuiButton>
              )}
          </Stack>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            href={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </form>
      <AcquireBookModal
        acquireBookOpen={acquireBookOpen}
        handleClose={() => {
          setAcquireBookOpen(false);
        }}
        row={singleBookAcquireDialogData}
      />
      {openAcquireBookNameDialog && (
        <AcquireBookEditModal
          open={openAcquireBookNameDialog}
          setOpen={setOpenAcquireBookNameDialog}
          handleClose={handleRefreshAfterEdit}
          pictureRequestId={Number(id)}
          data={data}
        />
      )}
      <ConfirmAnalyzeDialog
        open={openOwnerAnalyzeDialog}
        handleClose={() => {
          setOpenOwnerAnalyzeDialog(false);
        }}
        handleSubmit={handleOwnerAnalyzeButtonClick}
        type="owner"
      />
      <ConfirmAnalyzeDialog
        open={openAllMattersAnalyzeDialog}
        handleClose={() => {
          setOpenAllMattersAnalyzeDialog(false);
        }}
        handleSubmit={handleAllMattersAnalyzeButtonClick}
        type="allMatters"
      />
    </PagePaper>
  );
};
